import { Icon, Tabs } from '@amelio/ui-library';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ObjectiveLoadErrorMessage, ObjectiveSectionLoader } from '../ObjectivePageStates';
import ObjectiveCommentCard from './ObjectiveCommentCard';
import ObjectiveDiscussionComposer from './ObjectiveDiscussionComposer';
import ObjectiveMeasureUpdateCard from './ObjectiveMeasureUpdateCard';
import ObjectiveStructureUpdateCard from './ObjectiveStructureUpdateCard';

import type { ObjectiveComment } from '@/types/objectives';
import type { TabItem } from '@amelio/ui-library';

import { useGetUsersByIds } from '@/api/directory';
import { useGetObjectiveComments } from '@/api/objectives';
import { GLASS_SURFACE } from '@/components/objectives/objectiveStyles';
import { ObjectiveCommentKind } from '@/constants/ObjectiveCommentKind';

/** The thread's authors. Whoever a change mentions comes already named by the server. */
const collectAuthorIds = (comments: ObjectiveComment[]): string[] => [
    ...new Set(comments.map((comment) => comment.authorId).filter((id): id is string => !!id)),
];

type DiscussionFilter = 'all' | 'comments';

interface ObjectiveDiscussionProps {
    objectiveId: string;
}

/**
 * "Discussion": the objective's thread. Read-only history plus a rich-text composer -- there is
 * no edit or delete, entries are append-only. People's messages sit alongside the entries the
 * server appends when a key result's value moves or the objective's structure is saved, in one
 * list sorted by the server. Fetches the thread's authors in one request (they can differ from the
 * owner/participants the rest of the detail page already loaded) and its own current-user avatar,
 * so it stays a self-contained section a caller can drop in without wiring anything else.
 */
export default function ObjectiveDiscussion({ objectiveId }: ObjectiveDiscussionProps) {
    const { t } = useTranslation();
    // An empty thread, a thread still on its way and a thread that could not be read are the same
    // empty array: the "no comments yet" line waits for a successful answer.
    const {
        data: comments = [],
        isLoading: areCommentsLoading,
        isError: areCommentsInError,
    } = useGetObjectiveComments(objectiveId);
    const authorIds = useMemo(() => collectAuthorIds(comments), [comments]);
    const { data: people = [] } = useGetUsersByIds({
        userIds: authorIds,
        identifier: 'objective-discussion',
    });
    const peopleById = useMemo(
        () => new Map(people.map((person) => [person.id, person])),
        [people],
    );

    const [filter, setFilter] = useState<DiscussionFilter>('all');
    const hasActivity = comments.some((comment) => comment.kind !== ObjectiveCommentKind.COMMENT);
    const visibleComments =
        hasActivity && filter === 'comments'
            ? comments.filter((comment) => comment.kind === ObjectiveCommentKind.COMMENT)
            : comments;
    const filterTabs: TabItem[] = [
        { key: 'all', label: t('objectives.detail.discussion.filterAll') },
        { key: 'comments', label: t('objectives.detail.discussion.filterComments') },
    ];

    const renderComment = (comment: ObjectiveComment) => {
        const author = comment.authorId ? peopleById.get(comment.authorId) : undefined;

        if (comment.kind === ObjectiveCommentKind.STRUCTURE_UPDATE && comment.structureLines) {
            return (
                <ObjectiveStructureUpdateCard
                    key={comment.id}
                    comment={comment}
                    lines={comment.structureLines}
                    author={author}
                />
            );
        }

        if (comment.kind === ObjectiveCommentKind.MEASURE_UPDATE && comment.keyResultChange) {
            return (
                <ObjectiveMeasureUpdateCard
                    key={comment.id}
                    comment={comment}
                    change={comment.keyResultChange}
                    author={author}
                />
            );
        }

        return (
            <ObjectiveCommentCard
                key={comment.id}
                comment={comment}
                author={author}
            />
        );
    };

    const renderThread = () => {
        if (areCommentsLoading) {
            return <ObjectiveSectionLoader dataTestId={'objective-discussion-loading'} />;
        }

        if (areCommentsInError) {
            return (
                <ObjectiveLoadErrorMessage
                    message={t('objectives.detail.discussion.loadError')}
                    dataTestId={'objective-discussion-error'}
                />
            );
        }

        if (comments.length === 0) {
            return (
                <p
                    className={'obj-discussion-subtitle text-obj-muted italic'}
                    data-testid={'objective-discussion-empty'}
                >
                    {t('objectives.detail.discussion.empty')}
                </p>
            );
        }

        if (visibleComments.length === 0) {
            return (
                <p
                    className={'obj-discussion-subtitle text-obj-muted italic'}
                    data-testid={'objective-discussion-no-comment'}
                >
                    {t('objectives.detail.discussion.filterEmpty')}
                </p>
            );
        }

        return (
            <div data-testid={'objective-discussion-list'}>
                <div className={'obj-discussion-list-gap flex flex-col'}>
                    {visibleComments.map(renderComment)}
                </div>
            </div>
        );
    };

    return (
        <div
            className={GLASS_SURFACE}
            data-testid={'objective-detail-discussion'}
        >
            <div className={'obj-discussion-panel rounded-[16px]'}>
                <div className={'obj-discussion-stack flex flex-col'}>
                    {/* Wraps rather than switching direction at a breakpoint: the filter sits beside
                        the title while both fit, below it on a phone. */}
                    <div className={'flex flex-wrap items-center justify-between gap-4'}>
                        <div className={'obj-discussion-header-gap flex flex-col'}>
                            <div className={'obj-discussion-header-gap flex items-center'}>
                                <Icon
                                    name={'messages-message'}
                                    size={'lg'}
                                    colorClassName={'text-navy'}
                                    strokeWidth={1.5}
                                />
                                <h3 className={'obj-discussion-title text-navy font-medium'}>
                                    {t('objectives.detail.discussion.title')}
                                </h3>
                            </div>
                            <p className={'obj-discussion-subtitle text-obj-muted font-medium'}>
                                {t('objectives.detail.discussion.subtitle')}
                            </p>
                        </div>

                        {hasActivity && (
                            <Tabs
                                items={filterTabs}
                                activeKey={filter}
                                onChange={(key) => setFilter(key as DiscussionFilter)}
                                variant={'segmented'}
                                ariaLabel={t('objectives.detail.discussion.filterLabel')}
                                dataTestId={'objective-discussion-filter'}
                            />
                        )}
                    </div>

                    <div className={'obj-discussion-list-gap flex flex-col'}>
                        {renderThread()}

                        <ObjectiveDiscussionComposer objectiveId={objectiveId} />
                    </div>
                </div>
            </div>
        </div>
    );
}
